import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LanguageContext, getTranslation } from '../LanguageContext';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaSearchPlus, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './DetailPage.css';

const detailTranslations = {
  en: {
    back: "Back",
    loading: "Loading...",
    notFound: "Content not found",
    location: "Location",
    photoCount: "{count} photos",
    videoCount: "{count} videos",
    audioCount: "{count} audios",
    imageOf: "Image {current} of {total}"
  },
  fr: {
    back: "Retour",
    loading: "Chargement...",
    notFound: "Contenu introuvable",
    location: "Emplacement",
    photoCount: "{count} photos",
    videoCount: "{count} vidéos",
    audioCount: "{count} audios",
    imageOf: "Image {current} de {total}"
  },
  ar: {
    back: "رجوع",
    loading: "جاري التحميل...",
    notFound: "المحتوى غير موجود",
    location: "الموقع",
    photoCount: "{count} صور",
    videoCount: "{count} فيديوهات",
    audioCount: "{count} ملفات صوتية",
    imageOf: "صورة {current} من {total}"
  }
};

const toArabicNumerals = (str) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/\d/g, (digit) => arabicNumerals[digit]);
};

const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const DetailPage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const translate = (key) => getTranslation(detailTranslations, language, key);
  const isRTL = language === 'ar';

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(-1);

  const typeConfig = {
    activities: { endpoint: '/activities', color: '#1a5276', gradient: 'linear-gradient(135deg, #1a5276, #2e86c1)' },
    videos: { endpoint: '/videos', color: '#c0392b', gradient: 'linear-gradient(135deg, #c0392b, #e74c3c)' },
    photos: { endpoint: '/photos', color: '#0d6931', gradient: 'linear-gradient(135deg, #0d6931, #27ae60)' },
    depositStations: { endpoint: '/deposit-stations', color: '#6c3483', gradient: 'linear-gradient(135deg, #6c3483, #8e44ad)' },
    newspapers: { endpoint: '/newspapers', color: '#d35400', gradient: 'linear-gradient(135deg, #d35400, #e67e22)' }
  };

  const config = typeConfig[type];

  useEffect(() => {
    const fetchItem = async () => {
      if (!config) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`https://anpe-web-api.ddns.net/api2${config.endpoint}/${id}`);
        setItem(res.data.data);
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [type, id, config]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    
    // Always use English numerals, but get month name in the correct language
    const day = date.getDate();
    const year = date.getFullYear();
    
    let monthName;
    if (language === 'ar') {
      const arMonths = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
      monthName = arMonths[date.getMonth()];
    } else if (language === 'fr') {
      const frMonths = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      monthName = frMonths[date.getMonth()];
    } else {
      const enMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      monthName = enMonths[date.getMonth()];
    }
    
    return `${day} ${monthName} ${year}`;
  };

  const openModal = (images, index) => {
    setModalImages(images);
    setModalIndex(index);
    setModalImage(images[index]);
  };

  const closeModal = () => {
    setModalImage(null);
    setModalImages([]);
    setModalIndex(-1);
  };

  const modalPrev = (e) => {
    e.stopPropagation();
    if (modalIndex > 0) {
      setModalIndex(modalIndex - 1);
      setModalImage(modalImages[modalIndex - 1]);
    }
  };

  const modalNext = (e) => {
    e.stopPropagation();
    if (modalIndex < modalImages.length - 1) {
      setModalIndex(modalIndex + 1);
      setModalImage(modalImages[modalIndex + 1]);
    }
  };

  const getItemImages = (item) => {
    if (item.images && item.images.length > 0) return item.images;
    if (item.URLImages && item.URLImages.length > 0) return item.URLImages;
    return [];
  };

  if (loading) {
    return (
      <div className={`detail-page ${isRTL ? 'rtl' : ''}`}>
        <div className="detail-loading">
          <div className="spinner"></div>
          <p>{translate('loading')}</p>
        </div>
      </div>
    );
  }

  if (!item || !config) {
    return (
      <div className={`detail-page ${isRTL ? 'rtl' : ''}`}>
        <div className="detail-not-found">
          <h2>{translate('notFound')}</h2>
          <button className="detail-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> {translate('back')}
          </button>
        </div>
      </div>
    );
  }

  const allImages = getItemImages(item);
  const allVideos = [
    ...(item.videos || []).map(url => ({ type: 'url', url })),
    ...(item.Videos || []).map(v => ({ type: 'object', ...v })),
  ];

  return (
    <div className={`detail-page ${isRTL ? 'rtl' : ''}`}>
      {/* Hero Header */}
      <div className="detail-hero" style={{ background: config.gradient }}>
        <div className="detail-hero-content">
          <button className="detail-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> {translate('back')}
          </button>
          <h1 className="detail-hero-title">{item.title}</h1>
          {item.date && (
            <div className="detail-hero-date">
              <FaCalendarAlt /> {formatDate(item.date)}
            </div>
          )}
        </div>
        <div className="detail-hero-overlay"></div>
      </div>

      {/* Content Container */}
      <div className="detail-container">
        {/* Info Bar */}
        <div className="detail-info-bar">
          {item.location && (
            <span className="detail-info-item">
              <FaMapMarkerAlt /> {item.location}
            </span>
          )}
          {item.category && (
            <span className="detail-category-badge">{item.category}</span>
          )}
        </div>

        {/* Description */}
        {item.description && (
          <div className="detail-description">
            <p>{item.description}</p>
          </div>
        )}

        {/* Deposit Stations: Paired Image + Audio Cards */}
        {type === 'depositStations' && allImages.length > 0 && (
          <div className="detail-section">
            <div className="detail-station-cards">
              {allImages.map((img, i) => (
                <div key={i} className="detail-station-card">
                  <div className="detail-station-image" onClick={() => openModal(allImages, i)}>
                    <img src={img} alt="" loading="lazy" />
                    <div className="detail-gallery-overlay">
                      <FaSearchPlus />
                    </div>
                  </div>
                  {item.audios && item.audios[i] && (
                    <div className="detail-station-audio">
                      <span className="detail-audio-icon">🎙️</span>
                      <audio controls>
                        <source src={item.audios[i]} />
                      </audio>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Show remaining audios that don't have paired images */}
            {item.audios && item.audios.length > allImages.length && (
              <div className="detail-station-cards" style={{ marginTop: '20px' }}>
                {item.audios.slice(allImages.length).map((audioUrl, i) => (
                  <div key={i} className="detail-station-card">
                    <div className="detail-station-audio">
                      <span className="detail-audio-icon">🎙️</span>
                      <audio controls>
                        <source src={audioUrl} />
                      </audio>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Images Gallery (non-station types) */}
        {type !== 'depositStations' && allImages.length > 0 && (
          <div className="detail-section">
            <h2 className="detail-section-title">
              {language === 'ar' ? toArabicNumerals(translate('photoCount').replace('{count}', allImages.length)) : translate('photoCount').replace('{count}', allImages.length)}
            </h2>
            <div className="detail-gallery">
              {allImages.map((img, i) => (
                <div key={i} className="detail-gallery-item" onClick={() => openModal(allImages, i)}>
                  <img src={img} alt="" loading="lazy" />
                  <div className="detail-gallery-overlay">
                    <FaSearchPlus />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Videos */}
        {allVideos.length > 0 && (
          <div className="detail-section">
            <h2 className="detail-section-title">
              {language === 'ar' ? toArabicNumerals(translate('videoCount').replace('{count}', allVideos.length)) : translate('videoCount').replace('{count}', allVideos.length)}
            </h2>
            <div className="detail-videos">
              {allVideos.map((video, i) => {
                const url = video.type === 'url' ? video.url : video.URLVideo;
                const ytId = getYouTubeVideoId(url);
                return (
                  <div key={i} className="detail-video-item">
                    {ytId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
                        title={`${item.title} ${i + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video controls poster={video.URLImage || ''}>
                        <source src={url} />
                      </video>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Audios (only for non-station types) */}
        {type !== 'depositStations' && item.audios && item.audios.length > 0 && (
          <div className="detail-section">
            <h2 className="detail-section-title">
              {language === 'ar' ? toArabicNumerals(translate('audioCount').replace('{count}', item.audios.length)) : translate('audioCount').replace('{count}', item.audios.length)}
            </h2>
            <div className="detail-audios">
              {item.audios.map((audioUrl, i) => (
                <div key={i} className="detail-audio-item">
                  <span className="detail-audio-icon">🎙️</span>
                  <audio controls>
                    <source src={audioUrl} />
                  </audio>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Newspaper Content */}
        {item.content && (
          <div className="detail-section">
            {item.image && (
              <div className="detail-newspaper-image">
                <img src={item.image} alt={item.title} />
              </div>
            )}
            <div className="detail-newspaper-content">
              <p>{item.content}</p>
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div className="detail-modal" onClick={closeModal}>
          <button className="detail-modal-close" onClick={closeModal}><FaTimes /></button>
          {modalIndex > 0 && (
            <button className="detail-modal-nav detail-modal-prev" onClick={modalPrev}><FaChevronLeft /></button>
          )}
          <div className="detail-modal-body" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="" />
            <div className="detail-modal-info">
              {language === 'ar' ? toArabicNumerals(translate('imageOf').replace('{current}', modalIndex + 1).replace('{total}', modalImages.length)) : translate('imageOf').replace('{current}', modalIndex + 1).replace('{total}', modalImages.length)}
            </div>
          </div>
          {modalIndex < modalImages.length - 1 && (
            <button className="detail-modal-nav detail-modal-next" onClick={modalNext}><FaChevronRight /></button>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailPage;
